import { Component, inject, signal } from '@angular/core';
import { AuthService } from '../_services/auth.service';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { HlmInputDirective } from '@helipagos-ng/ui-input-helm';
import { HlmLabelDirective } from '@helipagos-ng/ui-label-helm';
import { HlmIconComponent } from '@helipagos-ng/ui-icon-helm';
import { Router, NavigationExtras } from '@angular/router';
import { LoginProcessAdapter } from '../_adapters/login-process.adapter';
import { toasterUtil } from '@hp-core/utils/toaster.util';
import { UserStoreService } from '@hp-core/services/user-store.service';
import { ERole } from '@helipagos/auth';
import { TranslateModule } from '@ngx-translate/core';
import { AuthContainerComponent } from '../_components/auth-container.component';
import { lucideEye, lucideEyeOff, lucideLoader2 } from '@ng-icons/lucide';
import { provideIcons } from '@ng-icons/core';
import { hasControlError } from '@hp-core/adapters/cybertron.adapter';
import { AUTH_CONFIG } from '../auth.config';
import Clarity from '@microsoft/clarity';
import { toast } from 'ngx-sonner';

@Component({
  selector: 'auth-login',
  standalone: true,
  imports: [
    HlmIconComponent,
    AuthContainerComponent,
    ReactiveFormsModule,
    HlmInputDirective,
    HlmLabelDirective,
    TranslateModule,
  ],
  providers: [provideIcons({ lucideEye, lucideEyeOff, lucideLoader2 })],
  templateUrl: './login.component.html',
})
export class LoginComponent {
  private readonly _loginService = inject(AuthService);
  private readonly _userStore = inject(UserStoreService);
  private readonly router = inject(Router);
  private readonly authConfig = inject(AUTH_CONFIG);
  protected accessErrorExists = false;

  loginForm!: FormGroup;
  isLoading = false;
  redirecting = signal(false);

  constructor(private _fb: FormBuilder) {
    this._createLoginForm();
  }

  protected forgotPassword() {
    this.router.navigate(['auth', 'forgot-password']);
  }

  private _createLoginForm() {
    this.loginForm = this._fb.group({
      username: ['', Validators.required],
      password: ['', Validators.required],
    });
  }

  public login() {
    if (this.loginForm.invalid) {
      this.loginForm.markAllAsTouched();
      return;
    }
    const username = this.loginForm.get('username')?.value;
    const password = this.loginForm.get('password')?.value;

    if (username.length > 0 || password.length > 0) {
      this.isLoading = true;

      this._loginService.loginProcess(username ?? '', password ?? '').subscribe({
        next: () => {
          this._userStore.getRole().subscribe((roleData) => {
            const role = roleData as unknown as keyof typeof ERole;

            if (this.authConfig) {
              const isRoleAllowed = role && this.authConfig.allowedRoles.some((allowed) => allowed === role[0]);
              if (isRoleAllowed) {
                const specialRole = this.authConfig.specialRolesConfig?.find((config) => {
                  return config.rol === role[0];
                });
                if (specialRole) {
                  this.navigateSafely(specialRole.url);
                } else {
                  this.navigateSafely(this.authConfig.urlRedirect);
                }
              } else {
                this.navigateSafely(['access-denied']);
              }
            }
          });
          this.isLoading = false;
        },
        error: (err: LoginProcessAdapter) => {
          this.accessErrorExists = true;
          toasterUtil(err.message);
          toast('HTTP_ERROR - Ocurrió un error procesando la sesión', {
            position: 'top-right',
            duration: 2000,
            description: 'Intentelo de nuevo más tarde. Si el problema persiste, contactar con soporte',
            action: {
              label: 'X',
              onClick: () => {
                console.log('login.system.error.unknown');
              },
            },
          });
          this.isLoading = false;
        },
        complete: () => {
          this.router.navigate(['home']);
          this.isLoading = false;
        },
      });
    }
  }

  protected async navigateSafely(commands: any[], extras?: NavigationExtras): Promise<boolean> {
    try {
      this.redirecting.set(true);
      const result = await this.router.navigate(commands, extras);
      return true;
    } catch (error) {
      console.error('error.navigation.redirection: ', error);
      this.redirecting.set(false);
      return false;
    } finally {
      this.redirecting.set(false);
    }
  }

  hasError(controlName: string, errorName: string = 'required'): boolean {
    return hasControlError(this.loginForm.get(controlName), errorName);
  }
}
